/* eslint-disable react/no-did-update-set-state */

// 508 Resources
// ---------------
// https://inclusive-components.design/tooltips-toggletips/
// http://w3c.github.io/aria-practices/#tooltip

import React, { PureComponent, Fragment, Children, cloneElement } from 'react';
import PropTypes from 'prop-types';

import TooltipPopup from './TooltipPopup';


export default class Tooltip extends PureComponent {
    static propTypes = {
        /** The tooltip's trigger. Must be must be an html element or a React
         * component that takes a innerRef prop that passes it to an html element. */
        children: PropTypes.node.isRequired,

        /** The content to display inside the popup */
        content: PropTypes.node.isRequired,

        /** The desired popup position */
        position: PropTypes.oneOf(['top', 'bottom', 'right', 'left', 'topLeft', 'topRight']),

        /** The maximum width of the popup, in pixels */
        maxWidth: PropTypes.number,

        /** The id of the popup */
        id: PropTypes.string,

        /** The ref you want to pass to the element that's triggering the tooltip
         * (i.e. what the Tooltip is wrapped around). Use this if the trigger
         * button needs it's own ref outside the context of the tooltip.
         * Must use React.createRef() */
        triggerRef: PropTypes.shape({
            current: PropTypes.any
        }),

        /** The following standard events can be added to Tooltip when they're
         * needed on the trigger, since the workings of the tooltip cause these
         * props on the trigger button to not work */
        onMouseEnter: PropTypes.func,
        onMouseLeave: PropTypes.func,
        onFocus: PropTypes.func,
        onBlur: PropTypes.func
    }

    static defaultProps = {
        position: 'top',
        maxWidth: 250,
        id: 'tooltip-component'
    };

    constructor(props) {
        super(props);
        this.triggerRef = props.triggerRef || React.createRef();

        this.state = {
            isVisible: false
        };
    }

    onMouseEnter = (event) => {
        const { onMouseEnter } = this.props;

        this.showTooltip();

        if (onMouseEnter) {
            onMouseEnter(event);
        }
    }

    onMouseLeave = (event) => {
        const { onMouseLeave } = this.props;

        this.hideTooltip();

        if (onMouseLeave) {
            onMouseLeave(event);
        }
    }

    onFocus = (event) => {
        const { onFocus } = this.props;

        this.showTooltip();

        if (onFocus) {
            onFocus(event);
        }
    }

    onBlur = (event) => {
        const { onBlur } = this.props;

        this.hideTooltip();

        if (onBlur) {
            onBlur(event);
        }
    }

    showTooltip = () => {
        if (!this.triggerRef.current.disabled) {
            this.setState({
                isVisible: true
            });
        }
    }

    hideTooltip = () => {
        this.setState({
            isVisible: false
        });
    }

    render() {
        const { children, content, maxWidth, position, id } = this.props;
        const { isVisible } = this.state;
        return (
            <Fragment>
                {(Children.map(children, (child) => {
                    const childType = typeof child.type;
                    const newProps = {
                        "aria-describedby": isVisible ? id : null,
                        onMouseEnter: this.onMouseEnter,
                        onMouseLeave: this.onMouseLeave,
                        onFocus: this.onFocus,
                        onBlur: this.onBlur
                    };

                    if (childType === 'string') {
                        newProps.ref = this.triggerRef;
                    }
                    else {
                        newProps.innerRef = this.triggerRef;
                    }

                    return cloneElement(child, newProps);
                }))}
                <TooltipPopup
                    id={id}
                    content={content}
                    position={position}
                    maxWidth={maxWidth}
                    isVisible={isVisible}
                    triggerRef={this.triggerRef} />
            </Fragment>
        );
    }
}